import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, Users, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { submitContactForm, ContactFormData } from '../services/contactService';
import { contactInfo } from '../config/contactInfo';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    subject: 'General Inquiry'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const result = await submitContactForm(formData);
      setSubmitResult({
        success: result.success,
        message: result.message
      });

      // Reset form if successful
      if (result.success) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          subject: 'General Inquiry'
        });
      }
    } catch (error) {
      setSubmitResult({
        success: false,
        message: 'An unexpected error occurred. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfoDisplay = [
    {
      icon: Mail,
      title: 'Email',
      details: [contactInfo.email.primary],
      color: 'text-primary-400'
    },
    {
      icon: Phone,
      title: 'Phone',
      details: [contactInfo.phone.display],
      color: 'text-secondary-400'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: [contactInfo.business.address.full],
      color: 'text-accent-400'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [contactInfo.business.hours.weekdays, contactInfo.business.hours.weekend],
      color: 'text-success-400'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold mb-6 text-slate-900 dark:text-white"
          >
            Get in <span className="gradient-text">Touch</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-slate-700 dark:text-gray-300 max-w-3xl mx-auto"
          >
            Ready to transform your healthcare practice? Let's discuss how BioAxis can help streamline your operations.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white/50 dark:bg-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <h2 className="text-3xl font-bold mb-8 text-slate-900 dark:text-white">Contact Information</h2>
              <div className="space-y-6">
                {contactInfoDisplay.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="glass-effect glass-effect-hover p-6 rounded-xl"
                  >
                    <div className="flex items-start gap-4">
                      <info.icon className={`w-6 h-6 ${info.color} mt-1 flex-shrink-0`} />
                      <div>
                        <h3 className="font-semibold text-lg mb-2 text-slate-900 dark:text-white">{info.title}</h3>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-slate-700 dark:text-gray-300">{detail}</p>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="glass-effect p-8 rounded-2xl">
                <h2 className="text-3xl font-bold mb-8 text-slate-900 dark:text-white">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2 text-slate-800 dark:text-gray-200">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/90 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder="Dr. John Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-slate-800 dark:text-gray-200">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/90 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder="john@medicalpractice.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2 text-slate-800 dark:text-gray-200">
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        pattern="[0-9+()\s-]{7,20}"
                        title="Please enter a valid mobile number, e.g. 082 123 4567"
                        autoComplete="tel"
                        className="w-full px-4 py-3 bg-white/90 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder="082 123 4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-2 text-slate-800 dark:text-gray-200">
                        Company/Practice
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/90 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
                        placeholder="Smith Medical Practice"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium mb-2 text-slate-800 dark:text-gray-200">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/90 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
                      >
                        <option value="General Inquiry" className="bg-white dark:bg-gray-800 text-slate-900 dark:text-white">General Inquiry</option>
                        <option value="Product Demo" className="bg-white dark:bg-gray-800 text-slate-900 dark:text-white">Product Demo</option>
                        <option value="Technical Support" className="bg-white dark:bg-gray-800 text-slate-900 dark:text-white">Technical Support</option>
                        <option value="Partnership" className="bg-white dark:bg-gray-800 text-slate-900 dark:text-white">Partnership</option>
                        <option value="Sales" className="bg-white dark:bg-gray-800 text-slate-900 dark:text-white">Sales</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2 text-slate-800 dark:text-gray-200">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/90 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your needs and how we can help..."
                    />
                  </div>

                  {/* Submit Result */}
                  {submitResult && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg flex items-center gap-3 ${
                        submitResult.success 
                          ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
                          : 'bg-red-500/20 border border-red-500/30 text-red-400'
                      }`}
                    >
                      {submitResult.success ? (
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      )}
                      <p className="text-sm">{submitResult.message}</p>
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    className={`w-full btn-primary flex items-center justify-center gap-2 ${
                      isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Users className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-6 text-slate-900 dark:text-white">Need Immediate Support?</h2>
            <p className="text-xl text-slate-700 dark:text-gray-300 mb-8">
              Our support team is standing by to help you with any technical issues or questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.support}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Support
              </motion.a>
              <motion.a
                href={`tel:${contactInfo.phone.primary}`}
                whileHover={{ scale: 1.05 }}
                className="btn-secondary"
              >
                Call Support
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 